import type { AgentMessage, AssistantContentBlock, AssistantMessage, ImageContent, SessionTreeNode, TextContent, ToolCallContent } from "./types";

const SKILL_BLOCK_RE = /<skill\s+name="[^"]+"\s+location="[^"]+">\r?\n[\s\S]*?\r?\n<\/skill>/g;
const WHOLE_SKILL_INVOCATION_RE = /^\s*<skill\s+name="[^"]+"\s+location="[^"]+">\r?\n[\s\S]*?\r?\n<\/skill>(?:\r?\n\r?\n([\s\S]*))?\s*$/;
const SKILL_BLOCK_START_RE = /^\s*<skill\s+name=/;
const SKILL_BLOCK_PARTIAL_RE = /<skill\s+name="[^"]+"(?:\s+location="[^"]+")?[\s\S]*$/;
const INTERNAL_IMAGE_ANALYSIS_RE = /\r?\n?\r?\n?<pi-web-internal-image-analysis>\r?\n[\s\S]*?\r?\n<\/pi-web-internal-image-analysis>/g;
const INTERNAL_IMAGE_INSTRUCTION_RE = /\r?\n?\r?\n?请调用\s+love-coach\s+skill\s+的能力，基于用户文字和内部图片上下文给最终建议。不要停留在图片描述，要判断关系阶段、情绪信号、风险点，并给出可直接发送的话术或下一步行动。/g;
const DEFAULT_SKILL_COMMAND_RE = /^\s*\/skill:love-coach\b\s*/i;
const LEGACY_IMAGE_ANALYSIS_RE = /\r?\n\r?\n【图片识别结果】[\s\S]*$/;
const WINDOWS_SKILL_PATH_RE = /[A-Za-z]:[^\s"'`<>]*[\\/]skills[\\/][^\s"'`<>]*/gi;
const POSIX_SKILL_PATH_RE = /(?:^|[\s"'`])\/[^\s"'`<>]*\/skills\/[^\s"'`<>]*/gi;

function redactSkillPaths(text: string): string {
  return text
    .replace(WINDOWS_SKILL_PATH_RE, "[内部能力文件已隐藏]")
    .replace(POSIX_SKILL_PATH_RE, (match) => `${match[0] === "/" ? "" : match[0]}[内部能力文件已隐藏]`);
}

export function containsSkillPath(value: unknown): boolean {
  if (typeof value === "string") {
    return value.replace(/\\/g, "/").toLowerCase().includes("/skills/");
  }
  if (Array.isArray(value)) return value.some(containsSkillPath);
  if (value && typeof value === "object") return Object.values(value).some(containsSkillPath);
  return false;
}

export function isHiddenSkillToolCall(block: ToolCallContent | unknown): block is ToolCallContent {
  if (!block || typeof block !== "object") return false;
  const candidate = block as Partial<ToolCallContent>;
  return candidate.type === "toolCall" && containsSkillPath(candidate.input);
}

export function getHiddenSkillToolCallIds(message: AgentMessage | Partial<AgentMessage>): Set<string> {
  const ids = new Set<string>();
  if (message.role !== "assistant" || !Array.isArray(message.content)) return ids;
  for (const block of message.content) {
    if (isHiddenSkillToolCall(block) && block.toolCallId) ids.add(block.toolCallId);
  }
  return ids;
}

function redactInternalImageAnalysis(text: string): string {
  const redacted = text
    .replace(INTERNAL_IMAGE_ANALYSIS_RE, "")
    .replace(INTERNAL_IMAGE_INSTRUCTION_RE, "")
    .replace(LEGACY_IMAGE_ANALYSIS_RE, "")
    .replace(DEFAULT_SKILL_COMMAND_RE, "")
    .trim();
  return redactSkillPaths(redacted);
}

export function redactSkillText(text: string): string {
  const wholeMatch = text.match(WHOLE_SKILL_INVOCATION_RE);
  if (wholeMatch) {
    const userMessage = wholeMatch[1] ? redactInternalImageAnalysis(wholeMatch[1]) : "";
    return userMessage || "已调用恋爱军师能力。";
  }

  const withoutImageAnalysis = redactInternalImageAnalysis(text);
  if (withoutImageAnalysis !== text.trim()) {
    return withoutImageAnalysis || "已调用恋爱军师能力。";
  }

  if (!text.includes("<skill")) return redactSkillPaths(text);

  const trimmed = text.trim();
  const withoutBlocks = text.replace(SKILL_BLOCK_RE, "").trim();
  if (withoutBlocks !== trimmed) {
    return withoutBlocks || "已调用恋爱军师能力。";
  }

  const withoutPartialBlock = text.replace(SKILL_BLOCK_PARTIAL_RE, "").trim();
  if (withoutPartialBlock !== trimmed) {
    return withoutPartialBlock || "已调用恋爱军师能力。";
  }

  return SKILL_BLOCK_START_RE.test(text) || SKILL_BLOCK_PARTIAL_RE.test(text)
    ? "已调用恋爱军师能力。"
    : text;
}

export function redactInternalPromptForUi(prompt: string): string {
  if (!prompt.trim()) return "";
  return "内部提示已隐藏。";
}

export function redactSkillMessage<T extends AgentMessage | Partial<AgentMessage>>(message: T): T {
  const content = message.content;
  if (typeof content === "string") {
    const redacted = redactSkillText(content);
    return redacted === content ? message : { ...message, content: redacted };
  }
  if (!Array.isArray(content)) return message;
  let changed = false;
  const next: Array<TextContent | ImageContent | AssistantContentBlock> = [];
  for (const block of content) {
    if (isHiddenSkillToolCall(block)) {
      changed = true;
      continue;
    }
    if (block.type !== "text") {
      next.push(block);
      continue;
    }
    const redacted = redactSkillText(block.text);
    if (redacted !== block.text) changed = true;
    next.push({ ...block, text: redacted } as TextContent);
  }
  return changed ? { ...message, content: next } as T & Partial<AssistantMessage> : message;
}

export function redactSkillMessages<T extends AgentMessage | Partial<AgentMessage>>(messages: T[]): T[] {
  const hiddenToolIds = new Set<string>();
  const redacted: T[] = [];
  for (const message of messages) {
    for (const id of getHiddenSkillToolCallIds(message)) hiddenToolIds.add(id);
    if (message.role === "toolResult" && typeof message.toolCallId === "string" && hiddenToolIds.has(message.toolCallId)) {
      continue;
    }
    redacted.push(redactSkillMessage(message));
  }
  return redacted;
}

export function redactSkillTree(nodes: SessionTreeNode[]): SessionTreeNode[] {
  return nodes.map((node) => {
    const entry = node.entry.type === "message"
      ? { ...node.entry, message: redactSkillMessage(node.entry.message) }
      : node.entry;
    return {
      ...node,
      entry,
      children: redactSkillTree(node.children),
    };
  });
}
